/**
 * Improvement Index Status Badge
 * 
 * Compact status badge showing whether the live governed improvement artifact
 * index is current, read-only, and execution-safe.
 * 
 * SAFETY: This is a read-only display component. No execution authority is granted.
 */

import React from 'react';

export interface IndexStatusBadgeData {
  indexStatus: 'current' | 'stale' | 'invalid' | 'demo_fixture';
  artifactCount: number;
  indexHash: string;
  executionAuthority: false;
  generatedAt: string;
}

interface ImprovementIndexStatusBadgeProps {
  badgeData: IndexStatusBadgeData;
}

export const ImprovementIndexStatusBadge: React.FC<ImprovementIndexStatusBadgeProps> = ({
  badgeData,
}) => {
  const getStatusColor = (status: IndexStatusBadgeData['indexStatus']): string => {
    switch (status) {
      case 'current':
        return '#10b981'; // green
      case 'stale':
        return '#f59e0b'; // orange
      case 'invalid':
        return '#ef4444'; // red
      case 'demo_fixture':
        return '#6b7280'; // gray
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: IndexStatusBadgeData['indexStatus']): string => {
    switch (status) {
      case 'current':
        return 'Live index current';
      case 'stale':
        return 'Index stale';
      case 'invalid':
        return 'Invalid index';
      case 'demo_fixture':
        return 'Demo fixture';
      default:
        return 'Unknown status';
    }
  };

  const truncatedHash = badgeData.indexHash.slice(0, 12);

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '4px',
        backgroundColor: getStatusColor(badgeData.indexStatus) + '20',
        border: `1px solid ${getStatusColor(badgeData.indexStatus)}`,
        fontSize: '12px',
        fontWeight: 500,
        color: getStatusColor(badgeData.indexStatus),
        gap: '8px',
      }}
    >
      <span>{getStatusText(badgeData.indexStatus)}</span>
      <span style={{ opacity: 0.7 }}>•</span>
      <span>{badgeData.artifactCount} artifacts</span>
      <span style={{ opacity: 0.7 }}>•</span>
      <span style={{ fontFamily: 'monospace' }}>{truncatedHash}</span>
    </div>
  );
};
